<template>
    <base-modal id="link-modal" :centered="true">
        <template slot="title">
            <span class="icon icon-100 icon-glyphicons-basic-paired align-middle mr-1"></span>
            <span class="align-middle">Page Link</span>
        </template>
        <template>
            <div class="row">
                <div class="col-12">
                    <div class="mb-3" v-show="!selectedPage.is_enabled">
                        <span class="badge badge-small badge-warning mr-1">Link Access Disabled</span>
                    </div>
                    <div class="pt-2">
                        <base-form
                            ref="linkSettingsForm"
                            v-model="selectedLink"
                            :fields="shareLinkFields"
                            :disabled="readOnly"
                        >
                            <template slot="passcode-footer">
                                <div class="col-12">
                                    <button
                                        class="btn btn-tiny btn-link mt-2"
                                        @click="refreshPasscode()"
                                        :disabled="readOnly"
                                    >
                                        <span class="icon icon-100 icon-glyphicons-basic-refresh mr-1"></span>
                                        <span>Regenerate</span>
                                    </button>
                                </div>
                            </template>
                        </base-form>
                        <copy-link-input ref="copyShareLinkInput" v-model="currentShareLinkUrl" class="mb-4 mt-4" />
                        <loading-status
                            v-show="linkSaving || linkSaved"
                            :loading="linkSaving"
                            loaded-text="Link settings saved"
                            loading-text="Saving link settings..."
                        ></loading-status>
                    </div>
                </div>
            </div>
        </template>
    </base-modal>
</template>

<script>
import http from "@/axios.js"
import BaseModal from "@/components/BaseModal"
import BaseForm from "@/components/BaseForm"
import CopyLinkInput from "@/components/CopyLinkInput"
import LoadingStatus from "@/components/LoadingStatus"
import debounce from "lodash.debounce"
import Utilities from "@/assets/js/utilities.js"

export default {
    name: "PublishPageLinkModal",
    props: {
        value: {
            type: Object,
            required: false,
        },
        readOnly: {
            type: Boolean,
            required: false,
            default: false,
        },
    },
    components: {
        BaseModal,
        BaseForm,
        CopyLinkInput,
        LoadingStatus,
    },
    data() {
        return {
            selectedPage: {
                label: null,
                share_link: {},
            },
            selectedLink: {},
            linkSaved: false,
            linkSaving: false,
            linkModalIsOpen: false,
        }
    },
    computed: {
        freeTierActive() {
            return this.$store.getters.organizationDetails.free_tier_active
        },
        shareLinkFields() {
            let vm = this
            return [
                {
                    name: "Require a passcode",
                    key: "require_passcode",
                    type: "switch",
                    condensedStyle: true,
                },
                {
                    name: "Passcode",
                    hideName: true,
                    key: "passcode",
                    type: "text",
                    placeholder: "Enter a 5 digit passcode",
                    maxlength: 5,
                    hidden(value) {
                        return !value.require_passcode
                    },
                    required(value) {
                        return value.require_passcode
                    },
                },
                {
                    type: "divider",
                    name: "Learner Reporting",
                },
                {
                    name: "Enable Learner Reporting",
                    key: "learner_reporting_enabled",
                    subtext: "Capture learner results and performance data.",
                    type: "switch",
                    disabled() {
                        return vm.freeTierActive
                    },
                    badge: vm.freeTierActive ? "Paid Only" : "",
                    condensedStyle: true,
                },
                {
                    name: "",
                    key: "identification_method",
                    type: "radio",
                    isSubOption: true,
                    options: [
                        {
                            label: "Anonymous",
                            subtext: "Learner analytics will be collected anonymously.",
                            value: "none",
                        },
                        {
                            label: "Email",
                            value: "email",
                            subtext: "Ask learners to enter their emails.",
                        },
                        {
                            label: "Student or Participant Number",
                            value: "student_number",
                            subtext: "Ask learners to enter their student or participant number.",
                        },
                    ],
                    hidden(value) {
                        return !value.learner_reporting_enabled
                    },
                    condensedStyle: true,
                },
            ]
        },
        currentShareLinkUrl() {
            if (!this.selectedLink) {
                return null
            }
            let baseUrl = `${window.location.origin}${this.selectedPage.access_url}`
            let idRequired = this.selectedLink.identification_method !== "none"
            let learnerInfoRequired = this.selectedLink.learner_reporting_enabled && idRequired
            if (this.selectedLink.require_passcode || learnerInfoRequired) {
                return `${baseUrl}/setup`
            }
            return baseUrl
        },
        currentLTILaunchLink() {
            return `${window.location.origin}/scenarios/?pageID=${this.selectedPage.id}&lmsLaunchID=${this.selectedPage.lms_launch_id}`
        },
    },
    watch: {
        // Watch for changes to the page in the parent, update the locally-editable
        // page object if the ID has changed.
        value(newValue, oldValue) {
            if (newValue.id && newValue.id !== oldValue.id) {
                this.selectedPage = newValue
                this.selectedLink = newValue.share_link
            }
        },
        // Save the link when its settings are edited.
        selectedLink: {
            handler(newVal, oldVal) {
                if ((newVal && !newVal.id) || !oldVal.id || newVal.id != oldVal.id) {
                    return
                }
                this.saveShareLink()
                this.$refs.copyShareLinkInput.reset()
            },
            deep: true,
        },
    },
    methods: {
        // Open the link modal.
        open() {
            this.$refs.copyShareLinkInput.reset()
            this.linkSaved = false
            $("#link-modal").modal("show")
        },
        refreshPasscode() {
            this.selectedLink.passcode = Utilities.getRandomFiveDigitStr()
        },
        // Save the page link settings.
        saveShareLink: debounce(function () {
            this.linkSaving = true
            let vm = this
            http.put("/api/pages/" + this.selectedPage.id + "/link/", this.selectedLink).finally(() => {
                vm.linkSaving = false
                vm.linkSaved = true
            })
        }, 150),
    },
}
</script>

<style>
</style>